// Common base selector to exclude accessibility widget elements
export const BASE_SELECTOR =
	":not(#accessibility-widget):not(#accessibility-widget *):not(#accessibility-widgetc):not(#accessibility-widgetc *):not(#accessibility-widget-parent):not(#accessibility-widget-parent *):not([data-radix-popper-content-wrapper]):not([data-radix-popper-content-wrapper] *):not([data-slot='sheet-content']):not([data-slot='sheet-content'] *)";

let femaleVoice = null;
let currentUtterance = null; // track current utterance so we can stop it reliably

export function removeStyle(id) {
	const el = document.getElementById(id);
	if (el) el.remove();
}
export function injectStyle(id, css) {
	let style = document.getElementById(id);
	if (!style) {
		style = document.createElement("style");
		style.id = id;
		document.head.appendChild(style);
	}
	style.innerText = css;
}
// Load voices once and select female
function initVoice() {
	const voices = speechSynthesis.getVoices();

	if (!voices.length) {
		// Retry when voices are loaded
		speechSynthesis.onvoiceschanged = initVoice;
		return;
	}

	// Pick preferred female voice
	femaleVoice =
		voices.find((v) => v.name.includes("Google US English")) || // Chrome
		voices.find((v) => v.name.includes("Samantha")) || // macOS
		voices.find((v) => v.name.includes("Zira")) || // Windows
		null;
}

initVoice();

export function stopSpeaking() {
	try {
		if (typeof speechSynthesis !== "undefined") {
			speechSynthesis.cancel();
		}
	} catch (e) {
		// swallow
	}
	currentUtterance = null;
}

export function speak(text, onEnd = null) {
	// Interrupt anything currently speaking (long page selection etc.)
	try {
		if (speechSynthesis.speaking || speechSynthesis.pending) {
			speechSynthesis.cancel();
		}
	} catch (e) {
		// ignore
	}

	const utterance = new SpeechSynthesisUtterance(text);
	utterance.lang = "en-US";
	if (femaleVoice) utterance.voice = femaleVoice;

	utterance.onend = () => {
		currentUtterance = null;
		if (onEnd) onEnd();
	};
	utterance.onerror = () => {
		currentUtterance = null;
	};

	currentUtterance = utterance;
	speechSynthesis.speak(utterance);
}

if (typeof window !== "undefined" && !window.__accessibilitySpeechCleanup) {
	window.addEventListener("beforeunload", () => {
		try {
			speechSynthesis.cancel();
		} catch {}
	});
	window.__accessibilitySpeechCleanup = true;
}

export function handleTextSelection() {
	const selectedText = window.getSelection().toString().trim();
	if (selectedText.length > 0) {
		speak(selectedText);
	}
}
export function enableReadingGuide() {
	if (document.getElementById("accessibility-widget-reading-guide")) return;

	const guide = document.createElement("div");
	guide.id = "accessibility-widget-reading-guide";
	guide.style.position = "fixed";
	guide.style.width = "300px";
	guide.style.height = "4px";
	guide.style.background = "#155eef";
	guide.style.borderRadius = "4px";
	guide.style.zIndex = "999999";
	guide.style.pointerEvents = "none";
	guide.style.transition = "top 0.05s ease-out, left 0.05s ease-out";

	document.body.appendChild(guide);

	const onMouseMove = (e) => {
		const offsetX = e.clientX - 150; // 300px / 2
		const offsetY = e.clientY;

		guide.style.left = `${offsetX}px`;
		guide.style.top = `${offsetY}px`;
	};

	window.addEventListener("mousemove", onMouseMove);
	guide.dataset.active = "true";

	// Save cleanup function globally
	window.__readingGuideCleanup = () => {
		window.removeEventListener("mousemove", onMouseMove);
		guide.remove();
	};
}

export function disableReadingGuide() {
	if (window.__readingGuideCleanup) {
		window.__readingGuideCleanup();
		delete window.__readingGuideCleanup;
	}
}
// Reading Mask
export function createReadingMask() {
	if (typeof document === "undefined") return;
	if (document.getElementById("accessibility-widget-reading-mask-overlay"))
		return;
	const mask = document.createElement("div");
	mask.id = "accessibility-widget-reading-mask-overlay";
	mask.style.position = "fixed";
	mask.style.top = "0";
	mask.style.left = "0";
	mask.style.width = "100%";
	mask.style.height = "100%";
	mask.style.pointerEvents = "none";
	mask.style.zIndex = "999999";
	mask.innerHTML = `
			<div id="mask-top" style="position:absolute;top:0;left:0;width:100%;height:40%;background:rgba(0,0,0,0.6);pointer-events:none;"></div>
			<div id="mask-bottom" style="position:absolute;bottom:0;left:0;width:100%;height:60%;background:rgba(0,0,0,0.6);pointer-events:none;"></div>
			<div id="reading-mask-window" style="position:absolute;left:0;width:100%;height:120px;border-top:3px solid #2563eb;border-bottom:3px solid #2563eb;pointer-events:none;"></div>`;
	document.body.appendChild(mask);
	const maskTop = document.getElementById("mask-top");
	const maskBottom = document.getElementById("mask-bottom");
	const maskWindow = document.getElementById("reading-mask-window");
	const moveHandler = (e) => {
		if (!maskWindow || !maskTop || !maskBottom) return;
		const top = Math.max(0, e.clientY - maskWindow.offsetHeight / 2);
		maskWindow.style.top = `${top}px`;
		maskTop.style.height = `${top}px`;
		maskBottom.style.top = `${top + maskWindow.offsetHeight}px`;
		maskBottom.style.height = `calc(100% - ${top + maskWindow.offsetHeight}px)`;
	};
	document.addEventListener("mousemove", moveHandler);
	window.__readingMaskCleanup = () => {
		document.removeEventListener("mousemove", moveHandler);
		const m = document.getElementById(
			"accessibility-widget-reading-mask-overlay",
		);
		if (m) m.remove();
	};
}
export function removeReadingMask() {
	if (window.__readingMaskCleanup) {
		window.__readingMaskCleanup();
		delete window.__readingMaskCleanup;
	} else {
		const m = document.getElementById(
			"accessibility-widget-reading-mask-overlay",
		);
		if (m) m.remove();
	}
}
export function applyBodyZoom(stepIndex) {
	const zoomLevels = [1.1, 1.2, 1.3, 1.4];
	const zoomValue = zoomLevels[stepIndex];

	const skipIds = new Set([
		"accessibility-widget",
		"accessibility-widgetc",
		"accessibility-widget-parent",
		"zn-sheet-widget",
	]);
	const skipAttributes = ["data-radix-popper-content-wrapper"];

	Array.from(document.body.children).forEach((el) => {
		const hasSkippedId = skipIds.has(el.id);
		const hasSkippedAttr = skipAttributes.some((attr) => el.hasAttribute(attr));

		if (!hasSkippedId && !hasSkippedAttr) {
			if (stepIndex === -1) {
				el.style.zoom = "";
			} else {
				el.style.zoom = `${zoomValue}`;
			}
			el.style.transition = "transform 0.3s ease";
		}
	});
}

export const applyContentAlignment = (stepIndex) => {
	const id = "accessibility-widget-style-contentAlignment";
	removeStyle(id);

	const styles = [
		`*${BASE_SELECTOR} { text-align: left !important; }`,
		`*${BASE_SELECTOR} { text-align: center !important; }`,
		`*${BASE_SELECTOR} { text-align: right !important; }`,
		`*${BASE_SELECTOR} { text-align: justify !important; }`,
	];

	if (styles[stepIndex]) {
		injectStyle(id, styles[stepIndex]);
	}
};
export const applyColorSaturation = (stepIndex) => {
	// Apply saturation at root (html) instead of body so it affects the full document,
	// and clear any legacy body filter that may have been set previously.
	const root = document.documentElement || document.body;
	if (!root) return;
	// Legacy cleanup
	if (document.body && document.body !== root) {
		document.body.style.filter = "";
	}
	const filters = ["saturate(0.5)", "saturate(3)", "saturate(0)"]; // step 0,1,2
	if (stepIndex >= 0 && stepIndex < filters.length) {
		root.style.filter = filters[stepIndex];
	} else {
		root.style.filter = "none"; // fallback/reset
	}
};

export const applyLetterSpacing = (stepIndex) => {
	const id = "accessibility-widget-style-adjustLetterSpacing";
	removeStyle(id);

	// If stepIndex is -1, reset and remove all base letter spacing attributes
	if (stepIndex === -1) {
		const elements = document.querySelectorAll("[data-base-letter-spacing]");
		elements.forEach((el) => {
			el.removeAttribute("data-base-letter-spacing");
			el.style.removeProperty("letter-spacing");
			if (el.hasAttribute("data-base-word-spacing")) {
				el.removeAttribute("data-base-word-spacing");
				el.style.removeProperty("word-spacing");
			}
		});
		return;
	}

	// Base increments (letter & word factors * font-size). Keep modest to avoid huge nav spacing.
	const spacingIncrements = [
		{ letter: 0.08, word: 0.06 }, // smaller start
		{ letter: 0.16, word: 0.12 },
		{ letter: 0.24, word: 0.18 },
	];

	// Unified selector list (exclude raw anchors to avoid nav/menu blowouts); we'll treat heading-contained anchors separately.
	const selectors = ["p", "div", "h1", "h2", "h3", "h4", "h5", "h6", "li"];

	// Uniform scale map; we will clamp outputs instead of boosting headings.
	const scaleMap = {
		p: 1,
		div: 1,
		a: 1,
		h1: 1,
		h2: 1,
		h3: 1,
		h4: 1,
		h5: 1,
		h6: 1,
		li: 1,
	};

	if (stepIndex >= 0 && stepIndex < spacingIncrements.length) {
		const increment = spacingIncrements[stepIndex];

		selectors.forEach((selector) => {
			const elements = document.querySelectorAll(`${selector}${BASE_SELECTOR}`);

			elements.forEach((element) => {
				// Skip if inside widget UI
				if (
					element.closest("#accessibility-widget") ||
					element.closest("#accessibility-widgetc") ||
					element.closest("#accessibility-widget-parent") ||
					element.closest("[data-radix-popper-content-wrapper]") ||
					element.closest('[data-slot="sheet-content"]')
				) {
					return;
				}

				// Skip navigation/menu contexts entirely
				if (
					element.closest("nav") ||
					element.closest('[class*="navigation"]') ||
					element.closest('[class*="menu"]') ||
					element.closest('[role="menubar"]')
				) {
					return;
				}

				// Milder spacing for anchors inside headings
				if (/^h[1-6]$/i.test(element.tagName)) {
					const headingLinks = element.querySelectorAll("a");
					headingLinks.forEach((link) => {
						// capture bases
						let hBaseLS = link.getAttribute("data-base-letter-spacing");
						if (!hBaseLS) {
							const ls = window.getComputedStyle(link).letterSpacing;
							hBaseLS = ls === "normal" ? 0 : parseFloat(ls);
							link.setAttribute("data-base-letter-spacing", hBaseLS);
						} else {
							hBaseLS = parseFloat(hBaseLS);
						}
						let hBaseWS = link.getAttribute("data-base-word-spacing");
						if (!hBaseWS) {
							const ws = window.getComputedStyle(link).wordSpacing;
							hBaseWS = ws === "normal" ? 0 : parseFloat(ws);
							link.setAttribute("data-base-word-spacing", hBaseWS);
						} else {
							hBaseWS = parseFloat(hBaseWS);
						}
						const fz = parseFloat(window.getComputedStyle(link).fontSize) || 16;
						let hNewLS = hBaseLS + increment.letter * 0.5 * fz; // milder
						let hNewWS = hBaseWS + increment.word * 0.4 * fz;
						if (hNewLS > 3.5) hNewLS = 3.5;
						if (hNewWS > 5) hNewWS = 5;
						link.style.letterSpacing = `${hNewLS}px`;
						link.style.wordSpacing = `${hNewWS}px`;
					});
				}

				// Base letter spacing capture
				let baseLetterSpacing = element.getAttribute(
					"data-base-letter-spacing",
				);
				if (!baseLetterSpacing) {
					const computedStyle = window.getComputedStyle(element);
					const currentLetterSpacing = computedStyle.letterSpacing;
					baseLetterSpacing =
						currentLetterSpacing === "normal"
							? 0
							: parseFloat(currentLetterSpacing);
					element.setAttribute("data-base-letter-spacing", baseLetterSpacing);
				} else {
					baseLetterSpacing = parseFloat(baseLetterSpacing);
				}

				const fontSize = parseFloat(window.getComputedStyle(element).fontSize);
				const scale = scaleMap[element.tagName.toLowerCase()] || 1;
				let newLetterSpacing =
					baseLetterSpacing + increment.letter * scale * fontSize;
				// Clamp excessive spacing (avoid > 6px which becomes unreadable in nav menus)
				if (newLetterSpacing > 6) newLetterSpacing = 6;
				element.style.letterSpacing = `${newLetterSpacing}px`;

				// Word spacing capture
				let baseWordSpacing = element.getAttribute("data-base-word-spacing");
				if (!baseWordSpacing) {
					const computedStyle = window.getComputedStyle(element);
					const currentWordSpacing = computedStyle.wordSpacing;
					baseWordSpacing =
						currentWordSpacing === "normal"
							? 0
							: parseFloat(currentWordSpacing);
					element.setAttribute("data-base-word-spacing", baseWordSpacing);
				} else {
					baseWordSpacing = parseFloat(baseWordSpacing);
				}

				let newWordSpacing =
					baseWordSpacing + increment.word * scale * fontSize * 0.9;
				if (newWordSpacing > 8) newWordSpacing = 8; // clamp word spacing too
				element.style.wordSpacing = `${newWordSpacing}px`;
			});
		});
	}
};

export function applyLineHeight(stepIndex) {
	const id = "accessibility-widget-style-lineHeight";
	removeStyle(id);

	// If stepIndex is -1, reset and remove all base line height attributes
	if (stepIndex === -1) {
		const elements = document.querySelectorAll("[data-base-line-height]");
		elements.forEach((el) => {
			el.removeAttribute("data-base-line-height");
			el.style.removeProperty("line-height");
		});
		return;
	}

	// Line height scaling multipliers for different steps
	const lineHeightMultipliers = [
		1.2, // Step 0: 20% increase
		1.4, // Step 1: 40% increase
		1.6, // Step 2: 60% increase
		1.8, // Step 3: 80% increase
	];

	if (stepIndex >= 0 && stepIndex < lineHeightMultipliers.length) {
		const multiplier = lineHeightMultipliers[stepIndex];

		// Apply to all elements that can have line height
		const selectors = [
			"h1",
			"h2",
			"h3",
			"h4",
			"h5",
			"h6",
			"p",
			//"div",
			"span",

			"dl",
			"dt",
			"dd",
			"td",
			"th",
			"label",
			"input",
			"textarea",
			"select",
			"button",
			"a",
			"small",
			".small",
		];

		selectors.forEach((selector) => {
			const elements = document.querySelectorAll(`${selector}${BASE_SELECTOR}`);

			elements.forEach((element) => {
				// Skip if element is inside accessibility widget
				if (
					element.closest("#accessibility-widget") ||
					element.closest("#accessibility-widgetc") ||
					element.closest("#accessibility-widget-parent") ||
					element.closest("[data-radix-popper-content-wrapper]") ||
					element.closest('[data-slot="sheet-content"]')
				) {
					return;
				}

				let baseLineHeight = element.getAttribute("data-base-line-height");

				// If no base line height is stored, capture current computed line height
				if (!baseLineHeight) {
					const computedStyle = window.getComputedStyle(element);
					const currentLineHeight = computedStyle.lineHeight;

					// Handle 'normal' line height by calculating it as 1.2 * font-size
					if (currentLineHeight === "normal") {
						const fontSize = parseFloat(computedStyle.fontSize);
						baseLineHeight = fontSize * 1.2; // Browser default for 'normal'
					} else {
						baseLineHeight = parseFloat(currentLineHeight);
					}

					element.setAttribute("data-base-line-height", baseLineHeight);
				} else {
					baseLineHeight = parseFloat(baseLineHeight);
				}

				// Apply scaled line height
				const newLineHeight = baseLineHeight * multiplier;
				element.style.lineHeight = `${newLineHeight}px`;
			});
		});
	}
}

export function applyFontSizing(stepIndex) {
	const id = "accessibility-widget-style-fontSizing";
	removeStyle(id);

	// If stepIndex is -1, reset and remove all base font size attributes
	if (stepIndex === -1) {
		// Remove all base font size attributes when resetting
		const elements = document.querySelectorAll("[data-base-font-size]");
		elements.forEach((el) => {
			el.removeAttribute("data-base-font-size");
			el.style.removeProperty("font-size");
		});
		return;
	}

	// Font scaling multipliers based on step
	const scaleMultipliers = [
		1.1, // Step 0: 110% (10% increase)
		1.25, // Step 1: 125% (25% increase)
		1.5, // Step 2: 150% (50% increase)
		1.75, // Step 3: 175% (75% increase)
	];

	if (stepIndex >= 0 && stepIndex < scaleMultipliers.length) {
		const multiplier = scaleMultipliers[stepIndex];

		// Store original font sizes and apply scaling
		const selectors = [
			"h1",
			"h2",
			"h3",
			"h4",
			"h5",
			"h6",
			"p",
			"div",
			"span",
			"li",
			"td",
			"th",
			"label",
			"a",
			"button",
			"input",
			"textarea",
			"select",
			"small",
			".small",
			"sub",
			"sup",
		];

		selectors.forEach((selector) => {
			const elements = document.querySelectorAll(`${selector}${BASE_SELECTOR}`);

			elements.forEach((element) => {
				// Skip if element is inside accessibility widget
				if (
					element.closest("#accessibility-widget") ||
					element.closest("#accessibility-widgetc") ||
					element.closest("#accessibility-widget-parent") ||
					element.closest("[data-radix-popper-content-wrapper]") ||
					element.closest('[data-slot="sheet-content"]')
				) {
					return;
				}

				let baseFontSize = element.getAttribute("data-base-font-size");

				// If no base font size is stored, capture current computed font size
				if (!baseFontSize) {
					const computedStyle = window.getComputedStyle(element);
					baseFontSize = parseFloat(computedStyle.fontSize);
					element.setAttribute("data-base-font-size", baseFontSize);
				} else {
					baseFontSize = parseFloat(baseFontSize);
				}

				// Apply scaled font size
				const newFontSize = baseFontSize * multiplier;
				element.style.fontSize = `${newFontSize}px`;
			});
		});
	}
}

export const scanPageStructure = () => {
	if (typeof document === "undefined") return null;
	// Get headings info
	const headings = Array.from(
		document.querySelectorAll("h1, h2, h3, h4, h5, h6"),
	).map((el) => ({
		text: el.innerText.trim(),
		level: el.tagName.toLowerCase(),
		id: el.id || null,
	}));
	// Get landmarks info
	const landmarks = Array.from(
		document.querySelectorAll("main, section, article, nav, aside"),
	).map((el) => ({
		tag: el.tagName.toLowerCase(),
		role: el.getAttribute("role"),
		ariaLabel: el.getAttribute("aria-label"),
		id: el.id || null,
	}));

	// get all links info
	const links = Array.from(document.querySelectorAll("a")).map((el) => ({
		text: el.innerText.trim(),
		href: el.href,
		target: el.target || null,
		id: el.id || null,
	}));

	return {
		headings,
		landmarks,
		links,
	};
};

// Dictionary functionality is now handled in React components
// Only keeping the utility debounce function if needed elsewhere

// Utility function for debouncing
const debounce = (func, wait) => {
	let timeout;
	return function executedFunction(...args) {
		const later = () => {
			clearTimeout(timeout);
			func(...args);
		};
		clearTimeout(timeout);
		timeout = setTimeout(later, wait);
	};
};

// ---------------- Tooltips ----------------
export function enableTooltips() {
	if (typeof document === "undefined") return;
	// If already enabled, do nothing
	if (window.__accessibilityTooltipCleanup) return;

	const tooltip = document.createElement("div");
	tooltip.id = "accessibility-widget-tooltips";
	tooltip.setAttribute("role", "tooltip");
	Object.assign(tooltip.style, {
		position: "fixed",
		zIndex: "999999",
		pointerEvents: "none",
		padding: "10px 20px",
		background: "#FFFB00",
		color: "#010101",
		fontSize: "14px",
		lineHeight: "1.2",
		fontWeight: "bold",
		borderRadius: "4px",
		maxWidth: "280px",
		overflowWrap: "break-word",
		transform: "translate(-50%, -120%)",
		whiteSpace: "normal",
		display: "none",
	});
	document.body.appendChild(tooltip);

	const pickText = (el) => {
		if (!el) return "";
		return (
			el.getAttribute("aria-label") ||
			el.getAttribute("data-tooltip") ||
			el.getAttribute("title") ||
			el.getAttribute("alt") ||
			(el.tagName === "IMG" && el.getAttribute("src")) ||
			(el.textContent || "").trim().slice(0, 120)
		);
	};

	let currentTarget = null;
	const mouseMove = (e) => {
		if (!tooltip || tooltip.style.display === "none") return;
		tooltip.style.left = e.clientX + "px";
		tooltip.style.top = e.clientY + "px";
	};
	const mouseOver = (e) => {
		const el = e.target.closest(
			'a, button, img, input, textarea, select, [role="button"], [aria-label], [data-tooltip]',
		);
		if (!el || document.getElementById("accessibility-widget")?.contains(el))
			return;
		const text = pickText(el);
		if (!text || text.length < 2) return;
		currentTarget = el;
		tooltip.textContent = text;
		tooltip.style.display = "block";
	};
	const mouseOut = (e) => {
		if (!currentTarget) return;
		if (!e.relatedTarget || !currentTarget.contains(e.relatedTarget)) {
			tooltip.style.display = "none";
			currentTarget = null;
		}
	};
	document.addEventListener("mousemove", mouseMove);
	document.addEventListener("mouseover", mouseOver, true);
	document.addEventListener("mouseout", mouseOut, true);

	window.__accessibilityTooltipCleanup = () => {
		document.removeEventListener("mousemove", mouseMove);
		document.removeEventListener("mouseover", mouseOver, true);
		document.removeEventListener("mouseout", mouseOut, true);
		tooltip.remove();
	};
}

export function disableTooltips() {
	if (window.__accessibilityTooltipCleanup) {
		window.__accessibilityTooltipCleanup();
		delete window.__accessibilityTooltipCleanup;
	}
}

// ---------------- Highlight Hover (JS-driven) ----------------
export function enableHighlightHover() {
	if (window.__highlightHoverCleanup) return; // already active
	if (typeof document === "undefined") return;

	// Inject style for class-based highlighting
	const styleId = "accessibility-widget-style-highlightHover-class";
	if (!document.getElementById(styleId)) {
		const style = document.createElement("style");
		style.id = styleId;
		style.textContent = `.highlight-hover { outline: 2px solid #ffbf00 !important; outline-offset:2px !important; background-color: rgba(255,191,0,0.12) !important; cursor: url('data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHZpZXdCb3g9IjAgMCAyNCAyNCIgZmlsbD0ibm9uZSI+PHBhdGggZD0iTTkgMy41VjJNNS4wNjA2NiA1LjA2MDY2TDQgNE01LjA2MDY2IDEzTDQgMTQuMDYwN0wxMyA1LjA2MDZMIDE0LjA2MDcgNE0zLjUgOUgyTTE1Ljg2NDUgMTYuMTg5NkwxMy4zNzI3IDIwLjgxN0MxMy4wODgxIDIxLjM0NTcgMTIuOTQ1NyAyMS42MSAxMi43NzQ1IDIxLjY3NjlDMTIuNjI1OSAyMS43MzQ5IDEyLjQ1ODUgMjEuNzE4NSAxMi4zMjQgMjEuNjMyOEMxMi4xNjg5IDIxLjUzNCAxMi4wODA2IDIxLjI0NzEgMTEuOTA4OCAyMC42NzMzTDguNDQ1MTkgOS40NDUyNUM4LjMwMDggOC45NzY1MSA4LjIyODYgOC43NDIxIDguMjg2OSA4LjU4MzgzQzguMzczOSA4LjQ0NTk1IDguNDQ1OTUgOC4zMzcyOSA4LjU4MzgzIDguMjg2N0M4Ljc0MjEzIDguMjI4NiA4Ljk3NjUgOC4zMDA4IDkuNDQ1MiA4LjQ0NTE5TDIwLjY3MzIgMTEuOTA4OEMyMS4yNDcgMTIuMDgwNiAyMS41MzkgMTIuMTY5IDIxLjYzMjcgMTIuMzRDMjEuNzE4NSAxMi40NTg2IDIxLjczNDggMTIuNjI1OSAyMS42NzY4IDEyLjc3NDVDMjEuNjEgMTIuOTQ1OCAyMS4zNDU2IDEzLjA4ODEgMjAuODE3IDEzLjM3MjhMMTYuMTg5NiAxNS44NjQ1QzE2LjExMSAxNS45MDY4IDE2LjA3MTcgMTUuOTI3OSAxNi4wMzc0IDE1Ljk1NTFDMTYuMDA2OCAxNS45NzkyIDE1Ljk3OTIgMTYuMDA2OCAxNS45NTUxIDE2LjAzNzRDMTUuOTI3OSAxNi4wNzE3IDE1LjkwNiAxNi4xMTEgMTUuODY0NSAxNi4xODk2WiIgc3Ryb2tlPSIjMDAwIiBzdHJva2Utd2lkdGg9IjIiIHN0cm9rZS1saW5lY2FwPSJyb3VuZCIgc3Ryb2tlLWxpbmVqb2luPSJyb3VuZCIvPjwvc3ZnPg=='), auto !important;
 }`;
		document.head.appendChild(style);
	}

	const skipSelectors = [
		"#accessibility-widget",
		"#accessibility-widgetc",
		"#accessibility-widget-parent",
		"[data-radix-popper-content-wrapper]",
	];

	function shouldSkip(el) {
		return skipSelectors.some((sel) => el && el.closest(sel));
	}

	const mouseOver = (e) => {
		const t = e.target;
		if (!t || shouldSkip(t)) return;
		t.classList.add("highlight-hover");
	};
	const mouseOut = (e) => {
		const t = e.target;
		if (t) t.classList.remove("highlight-hover");
	};

	document.addEventListener("mouseover", mouseOver, true);
	document.addEventListener("mouseout", mouseOut, true);

	window.__highlightHoverCleanup = () => {
		document.removeEventListener("mouseover", mouseOver, true);
		document.removeEventListener("mouseout", mouseOut, true);
		// Remove class from any remaining elements
		document
			.querySelectorAll(".highlight-hover")
			.forEach((el) => el.classList.remove("highlight-hover"));
	};
}

export function disableHighlightHover() {
	if (window.__highlightHoverCleanup) {
		window.__highlightHoverCleanup();
		delete window.__highlightHoverCleanup;
	}
}

// ---------------- Magnify Images ----------------
export function enableMagnifiImages() {
	if (window.__magnifiImagesCleanup) return; // already active
	if (typeof document === "undefined") return;

	const lensId = "accessibility-widget-image-magnifier-lens";
	let lens = document.getElementById(lensId);
	if (!lens) {
		lens = document.createElement("div");
		lens.id = lensId;
		Object.assign(lens.style, {
			position: "fixed",
			width: "150px",
			height: "150px",
			//border: "2px solid #2563eb",
			borderRadius: "50%",
			overflow: "hidden",
			pointerEvents: "none",
			zIndex: 999999,
			top: 0,
			left: 0,
			backgroundRepeat: "no-repeat",
			backgroundSize: "contain",
			display: "none",
			boxShadow: "0 2px 8px rgba(0,0,0,.3)",
			backdropFilter: "blur(2px)",
		});
		document.body.appendChild(lens);
	}

	const skipSelectors = [
		"#accessibility-widget",
		"#accessibility-widgetc",
		"#accessibility-widget-parent",
		"[data-radix-popper-content-wrapper]",
	];
	function shouldSkip(el) {
		return skipSelectors.some((sel) => el && el.closest(sel));
	}

	let currentImg = null;
	const scale = 2.5; // magnification level

	const onMouseOver = (e) => {
		const img = e.target.closest("img");
		if (!img || shouldSkip(img)) return;
		currentImg = img;
		const rect = img.getBoundingClientRect();
		// Use image src as background.
		lens.style.backgroundImage = `url('${img.currentSrc || img.src}')`;
		lens.style.backgroundSize = `${rect.width * scale}px ${
			rect.height * scale
		}px`;
		lens.style.display = "block";
	};

	const onMouseMove = (e) => {
		if (!currentImg) return;
		const rect = currentImg.getBoundingClientRect();
		const x = e.clientX - rect.left; // position inside image
		const y = e.clientY - rect.top;
		// Position lens near cursor (centered)
		const lensRectSize = 150;
		lens.style.left = e.clientX - lensRectSize / 2 + "px";
		lens.style.top = e.clientY - lensRectSize / 2 + "px";
		// Background position to create magnification effect
		const bgX = -(x * scale - lensRectSize / 2);
		const bgY = -(y * scale - lensRectSize / 2);
		lens.style.backgroundPosition = `${bgX}px ${bgY}px`;
	};

	const onMouseOut = (e) => {
		if (currentImg && !e.relatedTarget) {
			// leaving window
			currentImg = null;
			lens.style.display = "none";
			return;
		}
		if (
			currentImg &&
			e.relatedTarget &&
			!currentImg.contains(e.relatedTarget)
		) {
			// left the image entirely
			currentImg = null;
			lens.style.display = "none";
		}
	};

	document.addEventListener("mouseover", onMouseOver, true);
	document.addEventListener("mousemove", onMouseMove, true);
	document.addEventListener("mouseout", onMouseOut, true);

	window.__magnifiImagesCleanup = () => {
		document.removeEventListener("mouseover", onMouseOver, true);
		document.removeEventListener("mousemove", onMouseMove, true);
		document.removeEventListener("mouseout", onMouseOut, true);
		if (lens) lens.style.display = "none";
	};
}

export function disableMagnifiImages() {
	if (window.__magnifiImagesCleanup) {
		window.__magnifiImagesCleanup();
		delete window.__magnifiImagesCleanup;
	}
	const lens = document.getElementById(
		"accessibility-widget-image-magnifier-lens",
	);
	if (lens) lens.style.display = "none";
}

// ---------------- Big Cursor Variants (normalized) ----------------
// Normalize all custom SVGs to 64x64 so size looks consistent vs default. Add per-variant hotspots.
const BIG_CURSOR_VARIANTS = {
	default: {
		svg: `<svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
<path d="M2 2L10 22L10.3964 17.6393C10.5658 15.7755 11.3833 14.03 12.7067 12.7067C14.03 11.3833 15.7755 10.5658 17.6393 10.3964L22 10L2 2Z" fill="url(#paint0_linear_1799_3032)"/>
<path d="M9.99934 22.9995C9.79958 22.9993 9.60445 22.9394 9.43904 22.8274C9.27363 22.7154 9.1455 22.5565 9.07114 22.3711L1.07114 2.37114C0.998694 2.18942 0.981107 1.99045 1.02055 1.79885C1.06 1.60724 1.15474 1.4314 1.29307 1.29307C1.4314 1.15474 1.60724 1.06 1.79885 1.02055C1.99045 0.981107 2.18942 0.998694 2.37114 1.07114L22.3711 9.07114C22.5799 9.15445 22.7546 9.30568 22.8669 9.50044C22.9791 9.69519 23.0225 9.9221 22.99 10.1445C22.9575 10.367 22.851 10.572 22.6877 10.7265C22.5244 10.8809 22.3137 10.9759 22.0898 10.9959L17.729 11.3924C16.0982 11.5403 14.5708 12.2554 13.4129 13.4134C12.2549 14.5713 11.5398 16.0987 11.3919 17.7295L10.9954 22.0895C10.9751 22.3133 10.88 22.5237 10.7255 22.6868C10.571 22.85 10.3662 22.9564 10.1438 22.9889C10.096 22.9958 10.0477 22.9994 9.99934 22.9995ZM3.79424 3.79444L9.38064 17.7602L9.39934 17.5483C9.58952 15.4512 10.5091 13.4873 11.9981 11.9983C13.4871 10.5093 15.451 9.58972 17.5481 9.39954L17.76 9.38044L3.79424 3.79444Z" fill="#111927"/>
<path d="M9.29199 10.7062L7.29199 8.70619C7.19848 8.61347 7.1242 8.50318 7.07342 8.38168C7.02264 8.26017 6.99637 8.12983 6.9961 7.99814C6.99583 7.86645 7.02157 7.736 7.07185 7.61429C7.12213 7.49258 7.19595 7.38199 7.28909 7.28889C7.38222 7.19578 7.49282 7.12199 7.61455 7.07174C7.73628 7.0215 7.86673 6.99579 7.99842 6.9961C8.13011 6.9964 8.26045 7.02272 8.38194 7.07353C8.50343 7.12434 8.61369 7.19865 8.70639 7.29219L10.7064 9.29219C10.8925 9.48003 10.9967 9.73397 10.9961 9.99842C10.9955 10.2629 10.8901 10.5163 10.7031 10.7033C10.5161 10.8903 10.2626 10.9956 9.99814 10.9961C9.73368 10.9966 9.47978 10.8924 9.29199 10.7062Z" fill="#111927"/>
<defs>
<linearGradient id="paint0_linear_1799_3032" x1="3" y1="2" x2="15.5" y2="27" gradientUnits="userSpaceOnUse">
<stop offset="0.0702716" stop-color="#D1FCFF"/>
<stop offset="1" stop-color="#2970FF"/>
</linearGradient>
</defs>
</svg>`,
		hotspot: { x: 2, y: 2 },
	},
	complex: {
		svg: `<svg id="fi_2662849" enable-background="new 0 0 512.021 512.021" viewBox="0 0 512.021 512.021" xmlns="http://www.w3.org/2000/svg"><g><path d="m468.384 165.92-39.13-39.13 17.823-17.823c2.929-2.929 2.929-7.678 0-10.607s-7.677-2.93-10.607 0c-62.663 62.66-44.719 44.715-79.175 79.174-14.462 14.464-39.134 8.291-45.064-11.305l-34.137-112.819c-6.868-22.7 14.269-43.958 37.063-37.063l112.82 34.137c10.441 3.159 17.742 11.875 19.055 22.748.497 4.112 4.223 7.042 8.346 6.547 4.111-.497 7.043-4.232 6.547-8.345-2.037-16.869-13.38-30.398-29.604-35.307l-112.82-34.137c-26.585-8.046-51.446 9.38-56.695 33.084-14.367 5.861-25.086 18.393-28.475 33.728-.446 1.531-.854 3.556-1.418 6.34-4.061 20.104-6.412 44.581-5.912 67.936-24.483 5.774-48.52 17.159-68.849 33.165-16.773-11.544-36.404-21.623-58.465-29.992-28.27-10.726-59.152 7.271-63.871 36.996-4.122 25.957 13.547 50.3 39.506 54.422 11.959 1.899 22.88 4.483 32.614 7.681-18.049 53.159 3.449 109.702 56.345 128.983-7.97 2.422-15.374 3.68-21.939 3.68-15.793 0-28.641 12.848-28.641 28.64 0 9.18 4.45 17.869 11.903 23.243 3.361 2.422 8.048 1.663 10.471-1.697 2.422-3.36 1.662-8.047-1.697-10.47-3.555-2.563-5.677-6.703-5.677-11.076 0-7.521 6.119-13.64 13.641-13.64 13.752 0 30.088-4.369 47.364-12.631 3.59.452 7.253.758 10.981.921-1.369 8.101-1.369 16.153-.032 23.976-13.155 6.205-25.931 10.55-38.049 12.928-4.064.798-6.713 4.739-5.915 8.804.793 4.048 4.717 6.714 8.804 5.915 12.634-2.479 25.833-6.865 39.333-13.05 8.298 20.301 24.313 33.994 46.717 38.826 1.208 8.644 2.424 18.131 3.187 27.105 1.337 15.728 15.221 27.446 30.964 26.112 15.735-1.338 27.448-15.228 26.109-30.963-.784-9.232-1.974-18.773-3.093-26.984 6.459-2.104 13.025-4.696 19.659-7.782.16 2.934.251 5.796.251 8.543 0 15.792 12.848 28.64 28.64 28.64 15.793 0 28.641-12.848 28.641-28.64 0-15.446-1.897-33.346-5.116-48.637 8.742-11.811 14.508-25.467 14.833-40.279 11.33 8.059 27.077 6.834 37.01-3.269 11.07-11.261 10.914-29.431-.348-40.501-15.485-15.224-37.04-26.616-60.335-32.12 19.389-35.312 16.62-72.035-3.9-101.925l20.493-20.493 39.13 39.129c11.226 11.221 29.491 11.222 40.718-.001 11.221-11.227 11.221-29.491-.004-40.717zm-219.42-93.821c1.804-8.236 6.702-15.293 13.467-19.905.914 5.276-.11.423 26.372 88.401-15.237-2.494-31.041-2.582-46.854-.335-.283-21.382 1.665-43.798 7.015-68.161zm-135.384 159.321c-10.745-3.598-22.773-6.481-35.906-8.566-17.767-2.822-29.867-19.484-27.044-37.255 3.238-20.393 24.419-32.653 43.736-25.324 19.519 7.405 36.971 16.179 52.032 26.132-13.668 12.996-24.879 28.137-32.818 45.013zm170.845 265.551c-7.509.644-14.112-4.943-14.75-12.436-.676-7.952-1.686-16.224-2.737-23.987 9.121.296 18.175-.696 27.17-2.623 1.014 7.56 2.051 16.086 2.749 24.299.637 7.495-4.941 14.11-12.432 14.747zm72.834-28.599c-7.521 0-13.64-6.119-13.64-13.64 0-5.095-.257-10.491-.753-16.088 9.601-5.575 18.049-11.777 25.238-18.477 1.785 11.471 2.794 23.668 2.794 34.564.002 7.522-6.117 13.641-13.639 13.641zm9.33-68.402c-21.248 26.247-71.063 50.012-106.956 44.934-15.382-2.063-32.455-12.383-39.839-28.218-5.145-11.032-6.437-23.211-3.818-35.51 47.333-2.778 102.35-28.28 135.149-63.018 29.847 15.861 39.691 51.987 15.464 81.812zm55.174-67.228c5.364 5.273 5.438 13.926.167 19.289-5.273 5.364-13.927 5.439-19.29.165-2.964-2.914-6.528-5.707-10.608-8.332-5.364-15.228-15.849-28.456-30.981-37.339.79-1.03 1.565-2.065 2.318-3.105 22.376 4.331 43.932 15.104 58.394 29.322zm-64.55-47.307c-43.776 73.871-192.285 120.499-232.48 34.305-25.498-54.686 11.111-121.648 74.38-151.15 29.745-13.87 63.209-18.8 94.6-11.768l4.161 13.754c7.623 25.189 35.493 36.461 57.905 26.059 17.895 26.181 19.813 57.786 1.434 88.8zm100.565-89.406c-5.379 5.377-14.13 5.376-19.505.001l-39.129-39.129 19.504-19.504 39.129 39.129c5.377 5.378 5.377 14.127.001 19.503zm-200.184 45.14c-3.979-1.146-8.136 1.151-9.283 5.13-1.25 4.339-4.306 7.917-8.385 9.819s-8.784 1.943-12.911.111c-3.786-1.68-8.217.028-9.897 3.814s.027 8.217 3.813 9.897c8.081 3.585 17.316 3.512 25.334-.228 8.01-3.734 14.009-10.755 16.46-19.261 1.147-3.979-1.15-8.135-5.131-9.282zm43.573-27.658c-1.75-3.755-6.213-5.378-9.967-3.628s-5.378 6.213-3.627 9.967l5.36 11.496c1.756 3.767 6.223 5.373 9.967 3.628 3.754-1.75 5.378-6.213 3.627-9.967zm-119.65 55.793c-1.751-3.754-6.212-5.377-9.967-3.628-3.754 1.75-5.378 6.213-3.627 9.967l5.36 11.496c1.756 3.767 6.223 5.373 9.967 3.628 3.754-1.751 5.378-6.213 3.627-9.967z"/></g></svg>`,
		hotspot: { x: 4, y: 4 },
	},
	hand: {
		svg: `<svg viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg" id="fi_11643229"><g id="Layer_2" data-name="Layer 2"><g fill="%23232323"><path d="m9.01074 13.75a.7502.7502 0 0 1 -.75-.75v-9a2.75 2.75 0 0 1 5.5 0v7a.75.75 0 0 1 -1.5 0v-7a1.25 1.25 0 0 0 -2.5 0v9a.7502.7502 0 0 1 -.75.75z"/><path d="m17.01074 12.75a.7502.7502 0 0 1 -.75-.75v-1.5a1.75212 1.75212 0 0 0 -1.75-1.75.75114.75114 0 0 0 -.75.75v1.5a.75.75 0 0 1 -1.5 0v-1.5a2.25278 2.25278 0 0 1 2.25-2.25 3.25377 3.25377 0 0 1 3.25 3.25v1.5a.7502.7502 0 0 1 -.75.75z"/><circle cx="13" cy="13" r=".75"/><path d="m16.01074 22.75h-5.29736a4.72517 4.72517 0 0 1 -4.41309-2.99365l-.98633-2.479-1.6997-3.45312a2.81627 2.81627 0 0 1 4.75049-3.01563l.98193 1.84034a.74986.74986 0 1 1 -1.3252.70214l-.93359-1.76123a1.33473 1.33473 0 0 0 -1.78711-.24414 1.32006 1.32006 0 0 0 -.37109 1.75977l1.75439 3.564 1.00977 2.53223a3.23344 3.23344 0 0 0 3.01953 2.04829h5.29736a3.25377 3.25377 0 0 0 3.25-3.25v-6a1.25116 1.25116 0 0 0 -1.25-1.25.25048.25048 0 0 0 -.25.25v2a.75.75 0 0 1 -1.5 0v-2a1.75213 1.75213 0 0 1 1.75-1.75 2.7528 2.7528 0 0 1 2.75 2.75v6a4.7554 4.7554 0 0 1 -4.75 4.75z"/></g><path d="m15 19.75h-4a.75.75 0 0 1 0-1.5h4a.75.75 0 0 1 0 1.5z" fill="%237fbde7"/></g></svg>`,
		hotspot: { x: 6, y: 2 },
	},
	cluster: {
		svg: `<svg version="1.1" id="fi_624815" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 469.333 469.333"><g><g><path d="M438.827,186.347l-80.213-88.149c-15.872-15.872-41.728-15.893-57.749,0.128c-5.077,5.077-8.533,11.157-10.325,17.643 c-15.957-12.224-38.976-11.008-53.675,3.691c-5.056,5.077-8.512,11.157-10.347,17.621c-15.957-12.181-38.976-10.987-53.653,3.712 c-4.971,4.971-8.384,10.901-10.24,17.216l-37.803-37.803c-15.872-15.872-41.728-15.893-57.749,0.128 c-15.893,15.872-15.893,41.728,0,57.621l145.237,145.237l-86.144,13.525c-23.275,3.328-40.832,23.552-40.832,47.083 c0,17.643,14.357,32,32,32h201.152c31.339,0,60.8-12.203,82.965-34.368l33.557-33.557c22.144-22.123,34.325-51.563,34.325-82.859 C469.333,235.989,458.496,207.979,438.827,186.347z M419.925,332.992l-33.557,33.557c-18.133,18.133-42.24,28.117-67.883,28.117 H117.333c-5.888,0-10.667-4.779-10.667-10.667c0-12.971,9.685-24.128,22.677-25.984l106.987-16.811 c3.968-0.619,7.232-3.413,8.491-7.232c1.237-3.797,0.235-8-2.603-10.837L82.155,163.072c-7.573-7.573-7.573-19.904,0.107-27.605 c3.797-3.776,8.768-5.675,13.739-5.675c4.971,0,9.941,1.899,13.739,5.696l106.731,106.731c4.16,4.16,10.923,4.16,15.083,0 c2.069-2.091,3.115-4.821,3.115-7.552s-1.045-5.461-3.136-7.552l-43.584-43.584c-7.573-7.573-7.573-19.883,0.128-27.584 c7.552-7.552,19.904-7.552,27.456,0l43.605,43.605c4.16,4.16,10.923,4.16,15.083,0 c2.069-2.091,3.115-4.821,3.115-7.552 c0-2.731-1.045-5.461-3.136-7.552l-22.251-22.251c-7.573-7.573-7.573-19.883,0.128-27.584c7.552-7.552,19.904-7.552,27.456,0 l22.357,22.357c4.181,3.968,10.795,3.883,14.869-0.213c4.16-4.16,4.16-10.923,0-15.083l-0.917-0.917 c-3.669-3.669-5.696-8.555-5.696-13.739s2.005-10.048,5.803-13.845c7.595-7.552,19.883-7.531,27.115-0.363l79.872,87.787 C439.125,218.389,448,241.301,448,265.216 C448,290.816,438.037,314.88,419.925,332.992z"/></g></g><g><g><path d="M183.381,109.931C167.851,75.563,133.547,53.333,96,53.333c-52.928,0-96,43.072-96,96 c0,37.547,22.229,71.851,56.597,87.403c1.429,0.64,2.923,0.939,4.395,0.939c4.053,0,7.936-2.347,9.728-6.272 c2.411-5.376,0.021-11.691-5.333-14.123c-26.752-12.096-44.053-38.763-44.053-67.947c0-41.173,33.493-74.667,74.667-74.667 c29.184,0,55.851,17.301,67.947,44.053c2.411,5.376,8.747,7.787,14.101,5.333C183.424,121.621,185.813,115.307,183.381,109.931z"/></g></g></svg>`,
		hotspot: { x: 10, y: 10 },
	},
	focus: {
		svg: `<svg id="fi_10996694" viewBox="0 0 64 64" xmlns="http://www.w3.org/2000/svg"><path d="m58.53688 30.53688h-5.35029a21.26828 21.26828 0 0 0 -19.71232-19.71232v-5.35029a1.47428 1.47428 0 0 0 -2.94855 0v5.35029a21.26828 21.26828 0 0 0 -19.71232 19.71232h-5.35029a1.47444 1.47444 0 0 0 .00008 2.94854h5.35021a21.26828 21.26828 0 0 0 19.71232 19.71233v5.35025a1.47444 1.47444 0 0 0 2.94854-.00008v-5.35017a21.26828 21.26828 0 0 0 19.71233-19.71232h5.35029a1.47445 1.47445 0 0 0 0-2.94855zm-8.31659 0h-9.03747a9.31847 9.31847 0 0 0 -7.70855-7.70855v-9.03747a18.30368 18.30368 0 0 1 16.74602 16.74602zm-19.69457 0h-4.70889a6.37538 6.37538 0 0 1 4.70889-4.70888zm0 2.94855v4.70889a6.37538 6.37538 0 0 1 -4.70889-4.70889zm2.94855 0h4.70889a6.37538 6.37538 0 0 1 -4.70889 4.70889zm0-2.94855v-4.70888a6.37538 6.37538 0 0 1 4.70889 4.70889zm-2.94855-16.746v9.03747a9.31847 9.31847 0 0 0 -7.70855 7.70855h-9.03747a18.30368 18.30368 0 0 1 16.74602-16.74604zm-16.74602 19.69455h9.03747a9.31847 9.31847 0 0 0 7.70855 7.70857v9.03747a18.30368 18.30368 0 0 1 -16.74602-16.74604zm19.69457 16.746v-9.03743a9.31847 9.31847 0 0 0 7.70855-7.70855h9.03747a18.30368 18.30368 0 0 1 -16.74602 16.746z"/></svg>`,
		hotspot: { x: 8, y: 8 },
	},
};

// Size mappings for big cursor (lg = current 64 baseline)
const BIG_CURSOR_SIZES = { sm: 32, md: 48, lg: 64 };

function getStoredBigCursorSize() {
	try {
		return localStorage.getItem("accessibility-widget-bigCursor-size") || "lg";
	} catch {
		return "lg";
	}
}

// Expose for UI previews
export function getBigCursorVariantAssets() {
	const out = {};
	Object.entries(BIG_CURSOR_VARIANTS).forEach(([k, v]) => {
		if (typeof v === "string") out[k] = v;
		else if (v && v.svg) out[k] = encodeSvgToDataUrl(normalizeSvgSize(v.svg));
	});
	return out;
}

export function applyBigCursorStyle(variantKey) {
	if (typeof document === "undefined") return;
	const raw = BIG_CURSOR_VARIANTS[variantKey] || BIG_CURSOR_VARIANTS.default;
	let dataUrl = raw;
	let hx = 4,
		hy = 4;
	const sizeKey = getStoredBigCursorSize();
	const dimension = BIG_CURSOR_SIZES[sizeKey] || 64;
	if (typeof raw !== "string" && raw && raw.svg) {
		dataUrl = encodeSvgToDataUrl(normalizeSvgSize(raw.svg, dimension));
		if (raw.hotspot) {
			hx = raw.hotspot.x;
			hy = raw.hotspot.y;
		}
	}
	const id = "accessibility-widget-style-bigCursor";
	const css = `*:not(:is(#accessibility-widget, #accessibility-widget *, #accessibility-widgetc, #accessibility-widgetc *, #accessibility-widget-parent, #accessibility-widget-parent *, [data-radix-popper-content-wrapper], [data-radix-popper-content-wrapper] *)) { cursor: url('${dataUrl}') ${hx} ${hy}, auto !important; }
#accessibility-widget, #accessibility-widget *, #accessibility-widgetc, #accessibility-widgetc *, #accessibility-widget-parent, #accessibility-widget-parent *, [data-radix-popper-content-wrapper], [data-radix-popper-content-wrapper] * { cursor: auto !important; }`;
	removeStyle(id);
	injectStyle(id, css);
}

function normalizeSvgSize(svg, dim = 64) {
	try {
		let s = svg.trim();
		s = s.replace(/width="[^"]*"/gi, "");
		s = s.replace(/height="[^"]*"/gi, "");
		s = s.replace(
			/<svg([^>]*?)>/i,
			(m, g1) => `<svg${g1} width="${dim}" height="${dim}">`,
		);
		return s;
	} catch {
		return svg;
	}
}
function encodeSvgToDataUrl(svg) {
	return `data:image/svg+xml,${encodeURIComponent(svg)
		.replace(/'/g, "%27")
		.replace(/"/g, "%22")}`;
}

export function setBigCursorVariant(variantKey) {
	try {
		localStorage.setItem("accessibility-widget-bigCursor-variant", variantKey);
	} catch {}
	// Reapply if active
	const active =
		(typeof localStorage !== "undefined" &&
			JSON.parse(
				localStorage.getItem("accessibility-widget-active-features") || "[]",
			)) ||
		[];
	if (active.includes("bigCursor")) {
		applyBigCursorStyle(variantKey);
	}
	if (typeof window !== "undefined") {
		window.dispatchEvent(
			new CustomEvent("bigCursor-variant-changed", {
				detail: { variant: variantKey },
			}),
		);
	}
}

export function getBigCursorVariants() {
	return Object.keys(BIG_CURSOR_VARIANTS);
}

export function setBigCursorSize(sizeKey) {
	if (!BIG_CURSOR_SIZES[sizeKey]) return; // ignore invalid
	try {
		localStorage.setItem("accessibility-widget-bigCursor-size", sizeKey);
	} catch {}
	// Reapply style if feature active
	try {
		const active = JSON.parse(
			localStorage.getItem("accessibility-widget-active-features") || "[]",
		);
		if (active.includes("bigCursor")) {
			const variant =
				localStorage.getItem("accessibility-widget-bigCursor-variant") ||
				"default";
			applyBigCursorStyle(variant);
		}
	} catch {}
	if (typeof window !== "undefined") {
		try {
			window.dispatchEvent(
				new CustomEvent("bigCursor-size-changed", {
					detail: { size: sizeKey },
				}),
			);
		} catch {}
	}
}

export function getBigCursorSizes() {
	return Object.keys(BIG_CURSOR_SIZES);
}

// ---------------- Pointer Trail / Spotlight ----------------
// Basic implementation inspired by provided example but simplified for widget integration.
// Two modes: trail (colored line + fading dots) and spotlight (dark overlay with cutout circle)
export function enablePointerTrail(options = {}) {
	if (window.__pointerTrailCleanup) return; // already active
	if (typeof document === "undefined") return;
	// Persisted color
	const storedColor =
		(typeof localStorage !== "undefined" &&
			localStorage.getItem("accessibility-widget-pointerTrail-color")) ||
		null;
	const settings = {
		mode: options.mode || "trail", // 'trail' | 'spotlight'
		trailLength: options.trailLength || 20,
		trailSize: options.trailSize || 8,
		trailColor: options.trailColor || storedColor || "#3b82f6",
		spotlightSize: options.spotlightSize || 120,
		fadeMs: options.fadeMs || 1000,
	};

	const canvas = document.createElement("canvas");
	canvas.id = "accessibility-widget-pointer-trail";
	Object.assign(canvas.style, {
		position: "fixed",
		inset: 0,
		zIndex: 999998,
		pointerEvents: "none",
	});
	document.body.appendChild(canvas);
	const ctx = canvas.getContext("2d");
	const points = [];

	const resize = () => {
		canvas.width = window.innerWidth;
		canvas.height = window.innerHeight;
	};
	resize();
	window.addEventListener("resize", resize);

	const onMove = (e) => {
		const now = Date.now();
		points.push({ x: e.clientX, y: e.clientY, t: now });
		while (points.length > settings.trailLength) points.shift();
	};
	document.addEventListener("mousemove", onMove);

	let rafId;
	const render = () => {
		if (!ctx) return;
		ctx.clearRect(0, 0, canvas.width, canvas.height);
		const now = Date.now();
		if (settings.mode === "spotlight") {
			// dark overlay
			ctx.fillStyle = "rgba(0,0,0,0.7)";
			ctx.fillRect(0, 0, canvas.width, canvas.height);
			const last = points[points.length - 1];
			if (last) {
				const g = ctx.createRadialGradient(
					last.x,
					last.y,
					0,
					last.x,
					last.y,
					settings.spotlightSize,
				);
				g.addColorStop(0, "rgba(0,0,0,0)");
				g.addColorStop(1, "rgba(0,0,0,0.7)");
				ctx.globalCompositeOperation = "destination-out";
				ctx.fillStyle = g;
				ctx.beginPath();
				ctx.arc(last.x, last.y, settings.spotlightSize, 0, Math.PI * 2);
				ctx.fill();
				ctx.globalCompositeOperation = "source-over";
			}
		} else {
			// trail lines
			if (points.length > 1) {
				ctx.lineCap = "round";
				ctx.lineJoin = "round";
				for (let i = 1; i < points.length; i++) {
					const p = points[i];
					const prev = points[i - 1];
					const age = now - p.t;
					const alpha = Math.max(0, 1 - age / settings.fadeMs);
					ctx.strokeStyle = settings.trailColor;
					ctx.lineWidth = settings.trailSize;
					ctx.globalAlpha = alpha;
					ctx.beginPath();
					ctx.moveTo(prev.x, prev.y);
					ctx.lineTo(p.x, p.y);
					ctx.stroke();
				}
				ctx.globalAlpha = 1;
			}
			// dots
			points.forEach((p) => {
				const age = now - p.t;
				const alpha = Math.max(0, 1 - age / settings.fadeMs);
				const size = settings.trailSize * (0.5 + alpha * 0.5);
				ctx.globalAlpha = alpha;
				ctx.fillStyle = settings.trailColor;
				ctx.beginPath();
				ctx.arc(p.x, p.y, size / 2, 0, Math.PI * 2);
				ctx.fill();
			});
			ctx.globalAlpha = 1;
		}
		// cleanup old points
		for (let i = points.length - 1; i >= 0; i--) {
			if (now - points[i].t > settings.fadeMs) points.splice(i, 1);
		}
		rafId = requestAnimationFrame(render);
	};
	rafId = requestAnimationFrame(render);

	window.__pointerTrailState = { settings, canvas, points };
	window.__pointerTrailCleanup = () => {
		cancelAnimationFrame(rafId);
		document.removeEventListener("mousemove", onMove);
		window.removeEventListener("resize", resize);
		if (canvas.parentNode) canvas.parentNode.removeChild(canvas);
		delete window.__pointerTrailState;
	};
}

export function disablePointerTrail() {
	if (window.__pointerTrailCleanup) {
		window.__pointerTrailCleanup();
		delete window.__pointerTrailCleanup;
	}
}

// Update color without full re-init if possible, else re-init
export function setPointerTrailColor(color) {
	try {
		if (typeof localStorage !== "undefined") {
			localStorage.setItem("accessibility-widget-pointerTrail-color", color);
		}
	} catch {}
	// Determine active variant (if any)
	let currentVariant = null;
	try {
		currentVariant = localStorage.getItem(
			"accessibility-widget-pointerTrail-variant",
		);
	} catch {}
	// If rainbow, particle, geometric etc, we must fully reapply variant so color logic updates.
	if (
		currentVariant &&
		currentVariant !== "base" &&
		currentVariant !== "trail"
	) {
		// Re-apply variant creation
		if (window.__pointerTrailCleanup) {
			try {
				window.__pointerTrailCleanup();
			} catch {}
			delete window.__pointerTrailCleanup;
		}
		switch (currentVariant) {
			case "neon":
				__enableNeonTrail({ trailColor: color });
				break;
			case "particle":
				__enableParticleTrail({
					colors: [color, "#4ecdc4", "#45b7d1", "#96ceb4", "#feca57"],
				});
				break;
			case "geometric":
				__enableGeometricTrail({
					colors: [color, "#3498db", "#2ecc71", "#f39c12"],
				});
				break;
			case "line":
				__enableLineTrail({ trailColor: color });
				break;
			case "rainbow":
				// rainbow ignores color; just re-init to keep consistency
				__enableRainbowTrail();
				break;
			default:
				// treat unknown as base
				enablePointerTrail({ mode: "trail", trailColor: color });
		}
		return;
	}
	// Base trail update optimization
	if (window.__pointerTrailState) {
		window.__pointerTrailState.settings.trailColor = color;
	} else if (window.__pointerTrailCleanup) {
		disablePointerTrail();
		enablePointerTrail({ trailColor: color });
	}
}

// --- Pointer Trail Variant Implementations (Neon, Particle, Rainbow, Geometric, Line) ---
function __enableNeonTrail(options = {}) {
	if (window.__pointerTrailCleanup) return;
	const storedColor =
		(typeof localStorage !== "undefined" &&
			localStorage.getItem("accessibility-widget-pointerTrail-color")) ||
		"#00ffff";
	const settings = {
		trailLength: options.trailLength || 25,
		trailSize: options.trailSize || 12,
		trailColor: options.trailColor || storedColor,
		fadeMs: options.fadeMs || 1000,
		glowIntensity: options.glowIntensity || 20,
	};
	const canvas = document.createElement("canvas");
	canvas.id = "neon-pointer-trail";
	Object.assign(canvas.style, {
		position: "fixed",
		inset: 0,
		zIndex: 999998,
		pointerEvents: "none",
	});
	document.body.appendChild(canvas);
	const ctx = canvas.getContext("2d");
	const points = [];
	const resize = () => {
		canvas.width = window.innerWidth;
		canvas.height = window.innerHeight;
	};
	resize();
	window.addEventListener("resize", resize);
	const onMove = (e) => {
		const now = Date.now();
		points.push({ x: e.clientX, y: e.clientY, t: now });
		while (points.length > settings.trailLength) points.shift();
	};
	document.addEventListener("mousemove", onMove);
	let raf;
	const render = () => {
		ctx.clearRect(0, 0, canvas.width, canvas.height);
		const now = Date.now();
		points.forEach((p) => {
			const age = now - p.t;
			const alpha = Math.max(0, 1 - age / settings.fadeMs);
			const size = settings.trailSize * (0.5 + alpha * 0.5);
			if (alpha > 0) {
				ctx.shadowColor = settings.trailColor;
				ctx.shadowBlur = settings.glowIntensity;
				ctx.globalAlpha = alpha * 0.8;
				ctx.fillStyle = settings.trailColor;
				ctx.beginPath();
				ctx.arc(p.x, p.y, size, 0, Math.PI * 2);
				ctx.fill();
				ctx.shadowBlur = 0;
				ctx.globalAlpha = alpha;
				ctx.fillStyle = "#ffffff";
				ctx.beginPath();
				ctx.arc(p.x, p.y, size * 0.3, 0, Math.PI * 2);
				ctx.fill();
			}
		});
		ctx.globalAlpha = 1;
		ctx.shadowBlur = 0;
		const cutoff = now - settings.fadeMs;
		for (let i = points.length - 1; i >= 0; i--) {
			if (points[i].t < cutoff) points.splice(i, 1);
		}
		raf = requestAnimationFrame(render);
	};
	raf = requestAnimationFrame(render);
	window.__pointerTrailCleanup = () => {
		cancelAnimationFrame(raf);
		document.removeEventListener("mousemove", onMove);
		window.removeEventListener("resize", resize);
		if (canvas.parentNode) canvas.parentNode.removeChild(canvas);
	};
}
function __enableParticleTrail(options = {}) {
	if (window.__pointerTrailCleanup) return;
	const storedColor =
		(typeof localStorage !== "undefined" &&
			localStorage.getItem("accessibility-widget-pointerTrail-color")) ||
		"#ff6b6b";
	const settings = {
		particleCount: options.particleCount || 8,
		particleSize: options.particleSize || 4,
		particleSpeed: options.particleSpeed || 2,
		fadeMs: options.fadeMs || 800,
		colors: options.colors || [
			storedColor,
			"#4ecdc4",
			"#45b7d1",
			"#96ceb4",
			"#feca57",
		],
	};
	const canvas = document.createElement("canvas");
	canvas.id = "particle-pointer-trail";
	Object.assign(canvas.style, {
		position: "fixed",
		inset: 0,
		zIndex: 999998,
		pointerEvents: "none",
	});
	document.body.appendChild(canvas);
	const ctx = canvas.getContext("2d");
	const particles = [];
	const resize = () => {
		canvas.width = window.innerWidth;
		canvas.height = window.innerHeight;
	};
	resize();
	window.addEventListener("resize", resize);
	const onMove = (e) => {
		const now = Date.now();
		for (let i = 0; i < settings.particleCount; i++) {
			const angle =
				(Math.PI * 2 * i) / settings.particleCount + Math.random() * 0.5;
			const speed = settings.particleSpeed + Math.random() * 2;
			particles.push({
				x: e.clientX,
				y: e.clientY,
				vx: Math.cos(angle) * speed,
				vy: Math.sin(angle) * speed,
				t: now,
				color:
					settings.colors[Math.floor(Math.random() * settings.colors.length)],
				size: settings.particleSize + Math.random() * 3,
			});
		}
	};
	document.addEventListener("mousemove", onMove);
	let raf;
	const render = () => {
		ctx.clearRect(0, 0, canvas.width, canvas.height);
		const now = Date.now();
		particles.forEach((p) => {
			const age = now - p.t;
			const alpha = Math.max(0, 1 - age / settings.fadeMs);
			if (alpha > 0) {
				p.x += p.vx;
				p.y += p.vy;
				p.vy += 0.1;
				ctx.globalAlpha = alpha;
				ctx.fillStyle = p.color;
				ctx.beginPath();
				ctx.arc(p.x, p.y, p.size * alpha, 0, Math.PI * 2);
				ctx.fill();
			}
		});
		ctx.globalAlpha = 1;
		const cutoff = now - settings.fadeMs;
		for (let i = particles.length - 1; i >= 0; i--) {
			if (particles[i].t < cutoff) particles.splice(i, 1);
		}
		raf = requestAnimationFrame(render);
	};
	raf = requestAnimationFrame(render);
	window.__pointerTrailCleanup = () => {
		cancelAnimationFrame(raf);
		document.removeEventListener("mousemove", onMove);
		window.removeEventListener("resize", resize);
		if (canvas.parentNode) canvas.parentNode.removeChild(canvas);
	};
}
function __enableRainbowTrail(options = {}) {
	if (window.__pointerTrailCleanup) return;
	const settings = {
		trailLength: options.trailLength || 30,
		trailSize: options.trailSize || 10,
		fadeMs: options.fadeMs || 600,
		hueSpeed: options.hueSpeed || 2,
	};
	const canvas = document.createElement("canvas");
	canvas.id = "rainbow-pointer-trail";
	Object.assign(canvas.style, {
		position: "fixed",
		inset: 0,
		zIndex: 999998,
		pointerEvents: "none",
	});
	document.body.appendChild(canvas);
	const ctx = canvas.getContext("2d");
	const points = [];
	let hue = 0;
	const resize = () => {
		canvas.width = window.innerWidth;
		canvas.height = window.innerHeight;
	};
	resize();
	window.addEventListener("resize", resize);
	const onMove = (e) => {
		const now = Date.now();
		hue = (hue + settings.hueSpeed) % 360;
		points.push({ x: e.clientX, y: e.clientY, t: now, hue });
		while (points.length > settings.trailLength) points.shift();
	};
	document.addEventListener("mousemove", onMove);
	let raf;
	const render = () => {
		ctx.clearRect(0, 0, canvas.width, canvas.height);
		const now = Date.now();
		if (points.length > 1) {
			for (let i = 1; i < points.length; i++) {
				const p1 = points[i - 1];
				const p2 = points[i];
				const age = now - p2.t;
				const alpha = Math.max(0, 1 - age / settings.fadeMs);
				if (alpha > 0) {
					const gradient = ctx.createLinearGradient(p1.x, p1.y, p2.x, p2.y);
					gradient.addColorStop(0, `hsla(${p1.hue},100%,60%,${alpha})`);
					gradient.addColorStop(1, `hsla(${p2.hue},100%,60%,${alpha})`);
					ctx.strokeStyle = gradient;
					ctx.lineWidth = settings.trailSize * alpha;
					ctx.lineCap = "round";
					ctx.beginPath();
					ctx.moveTo(p1.x, p1.y);
					ctx.lineTo(p2.x, p2.y);
					ctx.stroke();
				}
			}
		}
		points.forEach((p) => {
			const age = now - p.t;
			const alpha = Math.max(0, 1 - age / settings.fadeMs);
			const size = settings.trailSize * (0.4 + alpha * 0.6);
			if (alpha > 0) {
				ctx.globalAlpha = alpha;
				ctx.fillStyle = `hsl(${p.hue},100%,60%)`;
				ctx.beginPath();
				ctx.arc(p.x, p.y, size, 0, Math.PI * 2);
				ctx.fill();
			}
		});
		ctx.globalAlpha = 1;
		const cutoff = now - settings.fadeMs;
		for (let i = points.length - 1; i >= 0; i--) {
			if (points[i].t < cutoff) points.splice(i, 1);
		}
		raf = requestAnimationFrame(render);
	};
	raf = requestAnimationFrame(render);
	window.__pointerTrailCleanup = () => {
		cancelAnimationFrame(raf);
		document.removeEventListener("mousemove", onMove);
		window.removeEventListener("resize", resize);
		if (canvas.parentNode) canvas.parentNode.removeChild(canvas);
	};
}
function __enableGeometricTrail(options = {}) {
	if (window.__pointerTrailCleanup) return;
	const storedColor =
		(typeof localStorage !== "undefined" &&
			localStorage.getItem("accessibility-widget-pointerTrail-color")) ||
		"#e74c3c";
	const settings = {
		trailLength: options.trailLength || 20,
		shapeSize: options.shapeSize || 15,
		fadeMs: options.fadeMs || 700,
		shapes: options.shapes || ["triangle", "square", "hexagon", "diamond"],
		colors: options.colors || [storedColor, "#3498db", "#2ecc71", "#f39c12"],
	};
	const canvas = document.createElement("canvas");
	canvas.id = "geometric-pointer-trail";
	Object.assign(canvas.style, {
		position: "fixed",
		inset: 0,
		zIndex: 999998,
		pointerEvents: "none",
	});
	document.body.appendChild(canvas);
	const ctx = canvas.getContext("2d");
	const points = [];
	const resize = () => {
		canvas.width = window.innerWidth;
		canvas.height = window.innerHeight;
	};
	resize();
	window.addEventListener("resize", resize);
	const drawShape = (x, y, size, shape, rotation = 0) => {
		ctx.save();
		ctx.translate(x, y);
		ctx.rotate(rotation);
		ctx.beginPath();
		switch (shape) {
			case "triangle":
				ctx.moveTo(0, -size);
				ctx.lineTo(-size * 0.866, size * 0.5);
				ctx.lineTo(size * 0.866, size * 0.5);
				break;
			case "square":
				ctx.rect(-size, -size, size * 2, size * 2);
				break;
			case "hexagon":
				for (let i = 0; i < 6; i++) {
					const a = (i * Math.PI) / 3;
					const px = size * Math.cos(a);
					const py = size * Math.sin(a);
					if (i === 0) ctx.moveTo(px, py);
					else ctx.lineTo(px, py);
				}
				break;
			case "diamond":
				ctx.moveTo(0, -size);
				ctx.lineTo(size, 0);
				ctx.lineTo(0, size);
				ctx.lineTo(-size, 0);
				break;
		}
		ctx.closePath();
		ctx.fill();
		ctx.restore();
	};
	const onMove = (e) => {
		const now = Date.now();
		const shape =
			settings.shapes[Math.floor(Math.random() * settings.shapes.length)];
		const color =
			settings.colors[Math.floor(Math.random() * settings.colors.length)];
		points.push({
			x: e.clientX,
			y: e.clientY,
			t: now,
			shape,
			color,
			rotation: Math.random() * Math.PI * 2,
		});
		while (points.length > settings.trailLength) points.shift();
	};
	document.addEventListener("mousemove", onMove);
	let raf;
	const render = () => {
		ctx.clearRect(0, 0, canvas.width, canvas.height);
		const now = Date.now();
		points.forEach((p) => {
			const age = now - p.t;
			const alpha = Math.max(0, 1 - age / settings.fadeMs);
			const size = settings.shapeSize * (0.3 + alpha * 0.7);
			if (alpha > 0) {
				ctx.globalAlpha = alpha;
				ctx.fillStyle = p.color;
				drawShape(p.x, p.y, size, p.shape, p.rotation + age * 0.002);
			}
		});
		ctx.globalAlpha = 1;
		const cutoff = now - settings.fadeMs;
		for (let i = points.length - 1; i >= 0; i--) {
			if (points[i].t < cutoff) points.splice(i, 1);
		}
		raf = requestAnimationFrame(render);
	};
	raf = requestAnimationFrame(render);
	window.__pointerTrailCleanup = () => {
		cancelAnimationFrame(raf);
		document.removeEventListener("mousemove", onMove);
		window.removeEventListener("resize", resize);
		if (canvas.parentNode) canvas.parentNode.removeChild(canvas);
	};
}
function __enableLineTrail(options = {}) {
	if (window.__pointerTrailCleanup) return;
	const storedColor =
		(typeof localStorage !== "undefined" &&
			localStorage.getItem("accessibility-widget-pointerTrail-color")) ||
		"#333333";
	const settings = {
		trailLength: options.trailLength || 15,
		lineWidth: options.lineWidth || 2,
		trailColor: options.trailColor || storedColor,
		fadeMs: options.fadeMs || 500,
	};
	const canvas = document.createElement("canvas");
	canvas.id = "line-pointer-trail";
	Object.assign(canvas.style, {
		position: "fixed",
		inset: 0,
		zIndex: 999998,
		pointerEvents: "none",
	});
	document.body.appendChild(canvas);
	const ctx = canvas.getContext("2d");
	const points = [];
	const resize = () => {
		canvas.width = window.innerWidth;
		canvas.height = window.innerHeight;
	};
	resize();
	window.addEventListener("resize", resize);
	const onMove = (e) => {
		const now = Date.now();
		points.push({ x: e.clientX, y: e.clientY, t: now });
		while (points.length > settings.trailLength) points.shift();
	};
	document.addEventListener("mousemove", onMove);
	let raf;
	const render = () => {
		ctx.clearRect(0, 0, canvas.width, canvas.height);
		const now = Date.now();
		if (points.length > 1) {
			ctx.lineCap = "round";
			ctx.lineJoin = "round";
			for (let i = 1; i < points.length; i++) {
				const p1 = points[i - 1];
				const p2 = points[i];
				const age = now - p2.t;
				const alpha = Math.max(0, 1 - age / settings.fadeMs);
				if (alpha > 0) {
					ctx.globalAlpha = alpha;
					ctx.strokeStyle = settings.trailColor;
					ctx.lineWidth = settings.lineWidth * alpha;
					ctx.beginPath();
					ctx.moveTo(p1.x, p1.y);
					ctx.lineTo(p2.x, p2.y);
					ctx.stroke();
				}
			}
		}
		ctx.globalAlpha = 1;
		const cutoff = now - settings.fadeMs;
		for (let i = points.length - 1; i >= 0; i--) {
			if (points[i].t < cutoff) points.splice(i, 1);
		}
		raf = requestAnimationFrame(render);
	};
	raf = requestAnimationFrame(render);
	window.__pointerTrailCleanup = () => {
		cancelAnimationFrame(raf);
		document.removeEventListener("mousemove", onMove);
		window.removeEventListener("resize", resize);
		if (canvas.parentNode) canvas.parentNode.removeChild(canvas);
	};
}

export function applyPointerTrailVariant(variant) {
	// Clean existing
	if (window.__pointerTrailCleanup) {
		try {
			window.__pointerTrailCleanup();
		} catch {}
		delete window.__pointerTrailCleanup;
	}
	try {
		localStorage.setItem("accessibility-widget-pointerTrail-variant", variant);
	} catch {}
	switch (variant) {
		case "neon":
			__enableNeonTrail();
			break;
		case "particle":
			__enableParticleTrail();
			break;
		case "rainbow":
			__enableRainbowTrail();
			break;
		case "geometric":
			__enableGeometricTrail();
			break;
		case "line":
			__enableLineTrail();
			break;
		default:
			enablePointerTrail({ mode: "trail" }); // base
	}
}

export function getPointerTrailVariant() {
	try {
		return (
			localStorage.getItem("accessibility-widget-pointerTrail-variant") ||
			"trail"
		);
	} catch {
		return "trail";
	}
}
